import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useDeleteConsumerImage from '@/hooks/queries/image-api/useDeleteConsumerImage';
import useGetConsumerImage from '@/hooks/queries/image-api/useGetConsumerImage';
import usePostFile from '@/hooks/queries/image-api/usePostFile';
import { Loader, UploadIcon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  { value: 'dniFront', label: 'DNI Front' },
  { value: 'dniBack', label: 'DNI Back' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'federativeLicense', label: 'Licencia Federativa' },
  { value: 'otros', label: 'Otros' }
];

const FormUploadConsumerImage: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();

  const { data: image, isFetching } = useGetConsumerImage(id);
  const {
    mutate: uploadFile,
    isPending: isUploading,
    isSuccess: isSuccessUpload,
    isError
  } = usePostFile(id);
  const {
    mutate: deleteImage,
    isPending: isDeleting,
    isSuccess: isSuccessDelete
  } = useDeleteConsumerImage(id);

  const isLoading =
    isFetching && !image && (isSuccessUpload || isSuccessDelete);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedType, setSelectedType] = useState<string>('dniFront');
  const [errorType, setErrorType] = useState<string | null>(null);

  const handleAddImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar si el tipo ya fue subido
      if (image?.some((img) => img.docType === selectedType)) {
        setErrorType(
          'Ya has subido un documento de este tipo. Elige otro tipo.'
        );
        return;
      }
      setErrorType(null);
      uploadFile({ file, docType: selectedType });
    }
  };

  const handleDelete = (imageId: string) => {
    deleteImage(imageId);
  };

  const handleDownload = async (imageUrl: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'imagen';
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('toast.errorDownloadImage'), { duration: 5000 });
    }
  };

  useEffect(() => {
    if (isError) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isError]);

  // render skeleton if is wating response with possible data
  if (isLoading)
    return (
      <div className="flex flex-col gap-3 p-8 mt-5">
        <Skeleton className="w-full h-7" />
        <Skeleton className="w-full h-7" />
        <Skeleton className="w-full h-11" />
      </div>
    );

  return (
    <div className="flex flex-col gap-3 p-8 mt-5">
      <div className="p-4 mb-2 border rounded-md bg-muted/50">
        <p className="flex items-center gap-2 mb-1 text-base font-semibold">
          {t('form.info')}
        </p>
        <ul className="pl-4 space-y-1 text-sm list-disc text-muted-foreground">
          <li>{t('form.uploadImageDescription')}</li>
          <li>{t('form.allowedTypes')}</li>
          <li>{t('form.documentTypes')}</li>
          <li className="font-medium text-red-600">
            {t('form.documentTypeWarning')}
          </li>
          <li className="font-medium text-blue-700">
            {t('form.documentTypeWebapp')}
          </li>
        </ul>
      </div>
      <div className="flex flex-col gap-3">
        {image &&
          image.length > 0 &&
          image.map((img) => (
            <div
              key={img.id}
              className="flex flex-col items-center gap-2 p-2 border rounded-md"
            >
              <div className="flex flex-col items-center w-full">
                {img.contentType === 'application/pdf' ? (
                  <div className="flex items-center gap-2">
                    <a
                      href={img.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      PDF
                    </a>
                  </div>
                ) : (
                  <Zoom>
                    <img
                      src={img.url}
                      alt="Imagen"
                      className="object-contain w-full h-auto cursor-pointer max-h-60"
                    />
                  </Zoom>
                )}
                <span className="text-xs mt-1 px-2 py-0.5 bg-gray-100 rounded-full border font-mono">
                  {img.docType}
                </span>
              </div>
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={() => handleDownload(img.url)}
                  disabled={isDeleting}
                  type="button"
                >
                  {t('common.downloadTable')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(img.id)}
                  disabled={isDeleting}
                  type='button'
                >
                  {isDeleting ? (
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <p>{t('form.delete')}</p>
                  )}
                </Button>
              </div>
            </div>
          ))}
      </div>
      <div className="flex flex-col gap-2 mt-2">
        <label className="mb-1 text-sm font-medium">
          {t('form.selectDocumentType')}
        </label>
        <select
          className="px-2 py-1 border rounded"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          {DOCUMENT_TYPES.map((type) => (
            <option
              key={type.value}
              value={type.value}
              disabled={image?.some((img) => img.docType === type.value)}
            >
              {type.label}
              {image?.some((img) => img.docType === type.value)
                ? ' (ya subido)'
                : ''}
            </option>
          ))}
        </select>
        {errorType && (
          <span className="mt-1 text-xs text-red-600">{errorType}</span>
        )}
        <Button
          onClick={handleAddImage}
          className="flex items-center w-full gap-2 mt-2"
          disabled={isUploading}
          type="button"
        >
          {isUploading ? (
            <Loader className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <>
              <UploadIcon className="w-5" />
              <p>{t('common.uploadImage')}</p>
            </>
          )}
        </Button>
        <input
          type="file"
          accept="image/*,application/pdf"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export default FormUploadConsumerImage;
